import { Category } from "@domain/models/Category";
import { type Payment, type IPaymentType, type PaymentPrototype, PaymentType } from "@domain/models/Payment";
import { type GQL, Model, Utils } from "@binale-tech/shared";
import { CategoryUtils } from "@domain/models/utils/CategoryUtils";
import type { CompanyAccountingYearContext } from "@domain/context/CompanyContext";
import { type ContextType } from "react";
import type { GenericRecord } from "@domain/models/GenericRecord";
import { type PaymentsCtxData } from "@domain/context/accountingData/PaymentsProvider";
import { ProductKeys } from "@domain/models/Product";
import { getRecordId } from "@accounting/shared/TableAccounting/components/tools";

export class PaymentUtils {
    protected static emptyPM: IPaymentType[] = [];

    static getPaymentType(category: Category, type: GQL.IPaymentType): IPaymentType {
        return {
            value: type,
            konto: category.getExtNum(Model.CompanyKontoExtMax),
        };
    }

    static getCompanyPaymentMethods(c: GQL.ICompany, year: number) {
        if (!c) {
            return this.emptyPM;
        }
        if (!c.accountingYears.includes(year)) {
            return this.emptyPM;
        }
        const vs: IPaymentType[] = [];
        if (c.kasseList.length) {
            c.kasseList
                .filter((v) => v.year === year)
                .forEach((kb) => {
                    const numCat = new Category(String(kb.accountNum), kb.name);
                    vs.push({
                        ...this.getPaymentType(numCat, PaymentType.OptionKasse),
                        isFavourite: kb.id === c.favouritePaymentType,
                        uuid: kb.id,
                    });
                });
        }
        if (c.bankList.length) {
            c.bankList
                .filter((v) => v.year === year)
                .forEach((bank) => {
                    const numCat = new Category(String(bank.accountNum), bank.name);
                    vs.push({
                        ...this.getPaymentType(numCat, PaymentType.OptionBank),
                        isFavourite: bank.id === c.favouritePaymentType,
                        uuid: bank.id,
                    });
                });
        }
        return vs;
    }

    static getVerrechnungPaymensAllocation(payments: Payment[], excludePaymentKeys: string[] = []) {
        const vrPaymentsLinkedKeys = new Map();
        payments
            .filter(({ key }) => !excludePaymentKeys.includes(key))
            .forEach((p) => {
                if (p.type.value === PaymentType.VERRECHNUNG.value) {
                    Object.keys(p.paymentSources).forEach((k) => {
                        if (!vrPaymentsLinkedKeys.has(k)) {
                            vrPaymentsLinkedKeys.set(k, 0);
                        }
                        vrPaymentsLinkedKeys.set(k, vrPaymentsLinkedKeys.get(k) + p.paymentSources[k]);
                    });
                }
            });
        return vrPaymentsLinkedKeys;
    }

    static getDefaultPaymentMethod({
        companyGQL,
        yearBanks,
        yearKbs,
    }: { companyGQL: GQL.ICompany } & Pick<ContextType<typeof CompanyAccountingYearContext>, "yearBanks" | "yearKbs">) {
        const favouritePayment = companyGQL.favouritePaymentType;
        if (favouritePayment) {
            const foundBank = yearBanks.find((value) => value.id === favouritePayment);
            const foundKasse = yearKbs.find((value) => value.id === favouritePayment);
            if (foundKasse) {
                const numCat = new Category(String(foundKasse.accountNum), foundKasse.name);
                return this.getPaymentType(numCat, PaymentType.OptionKasse);
            }
            if (foundBank) {
                const numCat = new Category(String(foundBank.accountNum), foundBank.name);
                return this.getPaymentType(numCat, PaymentType.OptionBank);
            }
        }
        if (yearBanks?.length) {
            const numCat = new Category(String(yearBanks[0].accountNum), yearBanks[0].name);
            return this.getPaymentType(numCat, PaymentType.OptionBank);
        }
        if (yearKbs?.length) {
            const numCat = new Category(String(yearKbs[0].accountNum), yearKbs[0].name);
            return this.getPaymentType(numCat, PaymentType.OptionKasse);
        }
        // switch (favouritePayment) {
        //     case PaymentType.BANK.value:
        //         return PaymentType.BANK;
        //     case PaymentType.KASSE.value:
        //         return PaymentType.KASSE;
        // }
        return null;
    }

    static getEuroFromCurrency(originalAmount: number, rate: number) {
        if (rate) {
            return Math.round(originalAmount / rate);
        } else {
            return originalAmount || 0;
        }
    }

    /**
     * Representation-signed value of `amount` (e.g. a source invoice's open brutto, or open − skonto)
     * when it is settled through `paymentRepresentationRecord`.
     *
     * Routes through the canonical {@link Utils.PaymentUtils.getPaymentDataFromRepresentationRecord}
     * so the Bank/KB ↔ FE sign inversion is applied identically in the modal reconciliation preview,
     * the booked split-child brutto, and the backend. An FE representation keeps the counterparty on
     * the record and the Geldkonto on the item (`isFeRecordInverted`), so its brutto is the *negation*
     * of the Bank/KB convention - hand-rolling `areSameSign ? x : -x` misses that flip (FIBU-914).
     */
    static getRepresentationSignedAmount(
        amount: number,
        paymentRepresentationRecord: GenericRecord,
        paymentRepresentationRecordProductKey: GQL.IProductKey,
        paymentSourceRecordProductKey: GQL.IProductKey,
    ): number {
        return Utils.PaymentUtils.getPaymentDataFromRepresentationRecord(
            amount,
            paymentRepresentationRecordProductKey,
            paymentSourceRecordProductKey,
            { category: paymentRepresentationRecord.category?.num },
            paymentRepresentationRecord.items.map((item) => ({ category: item.category?.num })),
        ).amount;
    }

    static getOpenBruttoFromSourceForPaymentRepresentationRecord(
        paymentRepresentationRecord: GenericRecord,
        paymentRepresentationRecordProductKey: GQL.IProductKey,
        paymentSourceRecord: GenericRecord,
        recordRelation: PaymentsCtxData["recordRelation"],
    ) {
        const relatedPayments = recordRelation.get(paymentSourceRecord.key);
        const openAmount = paymentSourceRecord.getOpenBrutto(relatedPayments);
        return this.getRepresentationSignedAmount(
            openAmount,
            paymentRepresentationRecord,
            paymentRepresentationRecordProductKey,
            paymentSourceRecord.getProductKey(),
        );
    }

    /**
     * Cash one source record moves when it is settled in full: (open − Skonto), signed into the
     * representation's S/H convention (honouring the Bank/KB ↔ FE inversion).
     * `skontoBetrag` is representation-signed (the line's sign, as typed in the dialog); it is converted to source sign once.
     *
     * `paymentRepresentationRecordProductKey` is passed in rather than read off the record: a record
     * composed by the inline form (`RecordFormUtils.constructRecord`) is a base `GenericRecord`, whose
     * `getProductKey()` is `null`, and the null reaches the shared util as
     * `invalid paymentRepresentationRecordProductKey` (BINALE-APP-42F). Callers holding a persisted
     * record pass `record.getProductKey()`; the form passes the module key from `TableViewContext`.
     */
    static getSettledCashFromSource(
        paymentRepresentationRecord: GenericRecord,
        paymentRepresentationRecordProductKey: GQL.IProductKey,
        paymentSourceRecord: GenericRecord,
        skontoBetrag: number,
        recordRelation: PaymentsCtxData["recordRelation"],
    ): number {
        const open = paymentSourceRecord.getOpenBrutto(recordRelation.get(paymentSourceRecord.key));
        // the sign conversion is its own inverse: representation sign in, source sign out
        const sourceSkonto = this.getRepresentationSignedAmount(
            skontoBetrag || 0,
            paymentRepresentationRecord,
            paymentRepresentationRecordProductKey,
            paymentSourceRecord.getProductKey(),
        );
        return this.getRepresentationSignedAmount(
            open - sourceSkonto,
            paymentRepresentationRecord,
            paymentRepresentationRecordProductKey,
            paymentSourceRecord.getProductKey(),
        );
    }

    /**
     * Reconciles a multi-source selection against the representation line it should reconstruct.
     * Summing SIGNED amounts (not magnitudes) makes opposite-direction sources - e.g. a credit note -
     * net out instead of piling up; the selection reconstructs the line exactly when `diff === 0`.
     *
     * Single source of truth for ConnectionModal's preview, `usePaymentMultiConnectionHandler`'s
     * split guard and the Kontenauskunft precondition - those must never disagree on what
     * reconciles, or the preview and the booked children would drift apart.
     */
    static getSplitReconciliation(
        paymentRepresentationRecord: GenericRecord,
        paymentRepresentationRecordProductKey: GQL.IProductKey,
        /** `skontoBetrag` is representation-signed (the line's sign). */
        entries: { sourceRecord: GenericRecord | undefined; skontoBetrag?: number }[],
        recordRelation: PaymentsCtxData["recordRelation"],
    ): { assigned: number; discount: number; gesamt: number; target: number; diff: number } {
        let assigned = 0;
        let discount = 0;
        let gesamt = 0;
        for (const { sourceRecord, skontoBetrag } of entries) {
            if (!sourceRecord || !paymentRepresentationRecord) {
                continue;
            }
            assigned += this.getSettledCashFromSource(
                paymentRepresentationRecord,
                paymentRepresentationRecordProductKey,
                sourceRecord,
                skontoBetrag,
                recordRelation,
            );
            gesamt += this.getOpenBruttoFromSourceForPaymentRepresentationRecord(
                paymentRepresentationRecord,
                paymentRepresentationRecordProductKey,
                sourceRecord,
                recordRelation,
            );
            discount += skontoBetrag || 0;
        }
        const target = paymentRepresentationRecord?.getBrutto() ?? 0;
        return { assigned, discount, gesamt, target, diff: target - assigned };
    }

    /**
     * The link a representation record carries, with the stored (source-signed) Skonto signed into the
     * representation's convention through {@link getRepresentationSignedAmount}, so an inverted FE gets
     * its extra flip (FIBU-914); a hand-rolled `areSameSign` missed it.
     */
    static toPrototype(
        payment: Payment,
        paymentRepresentationRecord: GenericRecord,
        paymentRecordRepresentationProductKey: GQL.IProductKey,
        paymentRecordSourceProductKey: GQL.IProductKey,
    ): PaymentPrototype {
        if (!payment) {
            return null;
        }
        const skontoBetrag = payment.skontoBetrag
            ? this.getRepresentationSignedAmount(
                  payment.skontoBetrag,
                  paymentRepresentationRecord,
                  paymentRecordRepresentationProductKey,
                  paymentRecordSourceProductKey,
              )
            : 0;
        return {
            skontoBetrag: Object.is(-0, skontoBetrag) ? 0 : skontoBetrag,
            sourceRecordKey: payment.sourceRecordKey,
        };
    }

    public static hasPaymentTypeChanged(oldPaymentType: Payment["type"], newPaymentType: Payment["type"]) {
        if (oldPaymentType.value !== newPaymentType.value) {
            return true;
        }
        const isKontoSame = CategoryUtils.areEqual(oldPaymentType.konto, newPaymentType.konto);
        return !isKontoSame;
    }

    /**
     * Determines if this is FE record from Anzahlungen payment or skonto
     * @param {GenericRecord} record
     * @param {Map<string, Payment[]>} paymentsData
     * @returns {boolean}
     */
    static readonly isFERecordAutoBlocked = (record: GenericRecord, paymentsData: Map<string, Payment[]>) => {
        if (record.getProductKey() !== ProductKeys.FE) {
            return false;
        }
        const p = paymentsData.get(getRecordId(record.key)) || [];
        if (p.length === 1) {
            const [payment] = p;
            if (payment.getAutoFERecordKeys().length > 0) {
                return true;
            }
        }
        return false;
    };

    static readonly isRecordSaldoTransfer = (record: GenericRecord) => {
        return Boolean(record.saldoSourceId);
    };

    static readonly isRepresentationRecordEditBlocked = (
        record: GenericRecord,
        paymentsData: Map<string, Payment[]>,
        yearConfig: GQL.ICompanyAccountingYear,
    ) => {
        if (yearConfig.taxation !== "IST") {
            return false;
        }
        if (!Utils.PaymentUtils.isProductPaymentRepresentation(record.getProductKey())) {
            return false;
        }
        const p = paymentsData.get(record.key) || [];
        if (p.length && record.items.length > 1) {
            return true;
        }
        return false;
    };
}
